import { createListenerMiddleware } from '@reduxjs/toolkit';

import { insertPanelButton } from 'FRONTEND/components/GenericActionBar/insertPanelButton';
import { HISTORY_TAB_INDEX, navigate, SUMMARY_TAB_INDEX } from 'FRONTEND/reducers/consultationViewSlice';
import { getConsultationEvents } from 'FRONTEND/utils/backendApi/consultationApi';
import { extractConsultationParamsFromUrl } from 'FRONTEND/utils/extractConsultationParamsFromUrl';
import { getConsultationTransitionPath, getConsultationEventsPath } from 'FRONTEND/utils/routes';
import { PJAX_TRANSITION_CONTAINER_SELECTOR, PJAX_EVENTS_CONTAINER_SELECTOR } from 'LEGACY/constants';

const consultationViewMiddleware = createListenerMiddleware();

// When tab changes to history,
// fetch the events and insert them in the panel,
// also fetch the current transition and insert it (defaults to comment).
consultationViewMiddleware.startListening({
  predicate: (action, currentState, previousState) => {
    return (
      currentState.consultationView.currentTabIndex !== previousState.consultationView.currentTabIndex &&
      currentState.consultationView.currentTabIndex === HISTORY_TAB_INDEX
    );
  },
  effect: async(action, { getState }) => {
    const consultationParams = extractConsultationParamsFromUrl(window.location.href);

    // WORKAROUND: Fetch events manually first to ensure backend has latest data and clear any caches
    // This prevents PJAX from receiving stale cached content
    await getConsultationEvents(consultationParams);

    const sequentialDiv = document.querySelector(PJAX_EVENTS_CONTAINER_SELECTOR);
    if (sequentialDiv) {
      $.pjax({
        url: getConsultationEventsPath(consultationParams), // pjax isn't fetching the results properly, so we fetch manually above.
        container: PJAX_EVENTS_CONTAINER_SELECTOR,
        push: false,
      });
    }

    // If a transition form should be displayed, we need to fetch it, insert it and scroll to it.
    const { displayedTransition } = getState().consultationView;
    const transitionDiv = document.querySelector(PJAX_TRANSITION_CONTAINER_SELECTOR);
    if (displayedTransition && transitionDiv) {
      $.pjax({
        url: getConsultationTransitionPath(consultationParams, displayedTransition),
        container: PJAX_TRANSITION_CONTAINER_SELECTOR,
        push: false, // Do not navigate to the transition URL.
      });
      if (action.payload.scrollToTransition) {
        transitionDiv.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    // Disable button in generic action bar, add button to the panel.
    insertPanelButton();
  },
});

// When a transition is clicked in the action bar while on the summary tab,
// navigate to the history tab and auto-scroll to the transition form.
// Note: we need to dispatch the navigate action because the reducer only wouldn't let us edit the payload to trigger a scroll later on.
consultationViewMiddleware.startListening({
  predicate: (action, currentState, previousState) => {
    return (
      currentState.consultationView.displayedTransition !== previousState.consultationView.displayedTransition &&
      currentState.consultationView.displayedTransition != null &&
      currentState.consultationView.currentTabIndex === SUMMARY_TAB_INDEX
    );
  },
  effect: (action, { dispatch, getState }) => {
    const hasCommentRights = getState().user.rights.includes('consultations-comment');
    dispatch(navigate({ tabIndex: HISTORY_TAB_INDEX, scrollToTransition: true, hasCommentRights }));
  },
});


export default consultationViewMiddleware.middleware;
